/**
 * Page /admin/notifications — configuration des emails admin envoyés
 * automatiquement quand un événement arrive (nouveau devis, lead CEE, etc.).
 *
 * Branchée sur /api/admin/notifications/* (router notifications_router.py).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    Bell, BellRing, BellOff, Plus, X, Send, Loader2, Mail, Save, MessageCircle, ExternalLink,
} from "lucide-react";

function _extractError(e) {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
    if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
    return e?.message || "Erreur inconnue";
}

export default function AdminNotifications() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [recipientInput, setRecipientInput] = useState("");

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/notifications/settings");
            setData(data);
        } catch (e) {
            toast.error("Lecture config : " + _extractError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    function toggle(typeId) {
        setData((d) => ({
            ...d,
            triggers: {
                ...d.triggers,
                [typeId]: {
                    ...d.triggers[typeId],
                    enabled: !(d.triggers[typeId]?.enabled ?? true),
                },
            },
        }));
    }

    function toggleWhatsAppPerTrigger(typeId) {
        setData((d) => ({
            ...d,
            triggers: {
                ...d.triggers,
                [typeId]: {
                    ...d.triggers[typeId],
                    whatsapp: !(d.triggers[typeId]?.whatsapp ?? true),
                },
            },
        }));
    }

    function toggleWhatsAppGlobal() {
        setData((d) => ({
            ...d,
            whatsapp: { ...d.whatsapp, enabled: !(d.whatsapp?.enabled ?? false) },
        }));
    }

    function setWhatsAppRecipient(v) {
        setData((d) => ({ ...d, whatsapp: { ...d.whatsapp, recipient: v } }));
    }

    function addRecipient() {
        const email = recipientInput.trim().toLowerCase();
        if (!email || !email.includes("@")) {
            toast.error("Email invalide");
            return;
        }
        if (data.recipients.includes(email)) return;
        setData((d) => ({ ...d, recipients: [...d.recipients, email] }));
        setRecipientInput("");
    }

    function removeRecipient(email) {
        setData((d) => ({ ...d, recipients: d.recipients.filter((r) => r !== email) }));
    }

    async function save() {
        if (!data) return;
        setSaving(true);
        try {
            const { data: updated } = await api.put("/admin/notifications/settings", {
                triggers: data.triggers,
                recipients: data.recipients,
                whatsapp: data.whatsapp || { enabled: false, recipient: "" },
            });
            setData(updated);
            toast.success("Notifications enregistrées");
        } catch (e) {
            toast.error("Sauvegarde : " + _extractError(e));
        } finally {
            setSaving(false);
        }
    }

    async function sendTest() {
        setTesting(true);
        try {
            const { data: res } = await api.post("/admin/notifications/test", {});
            if (res.ok) {
                toast.success(`Email de test envoyé à ${res.sent_to.join(", ")}`);
            } else {
                toast.error(res.error || "Test KO");
            }
        } catch (e) {
            toast.error("Test : " + _extractError(e));
        } finally {
            setTesting(false);
        }
    }

    if (loading) {
        return (
            <div className="em-container py-32 text-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Chargement…
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="em-container py-8 max-w-4xl">
            <p className="em-eyebrow">Communications</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">
                Notifications mail
            </h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">
                Reçois un email automatique dès qu'un événement arrive sur le site (nouveau devis,
                lead CEE…). La config SMTP est partagée avec la Boîte mail.
            </p>

            {/* Destinataires */}
            <section className="rounded-3xl border border-border bg-card p-6 mt-8">
                <div className="flex items-center gap-2 mb-4">
                    <Mail className="h-5 w-5 text-primary" />
                    <h2 className="font-display text-xl font-bold text-foreground">Destinataires</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                    Les emails listés ici recevront toutes les notifications activées ci-dessous.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                    {data.recipients.length === 0 ? (
                        <p className="text-sm italic text-muted-foreground">Aucun destinataire — ajoute au moins une adresse pour activer les notifications.</p>
                    ) : (
                        data.recipients.map((r) => (
                            <span key={r} className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-medium">
                                {r}
                                <button
                                    onClick={() => removeRecipient(r)}
                                    className="hover:bg-primary/20 rounded-full p-0.5"
                                    title="Supprimer"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </span>
                        ))
                    )}
                </div>
                <div className="flex gap-2">
                    <input
                        type="email"
                        value={recipientInput}
                        onChange={(e) => setRecipientInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient())}
                        placeholder="email@example.com"
                        className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    />
                    <button
                        onClick={addRecipient}
                        className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold border-2 border-border bg-card hover:border-primary/40"
                    >
                        <Plus className="h-4 w-4" /> Ajouter
                    </button>
                </div>
            </section>

            {/* WhatsApp */}
            <section className="rounded-3xl border border-border bg-card p-6 mt-6">
                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                    <div className="flex items-center gap-2">
                        <MessageCircle className="h-5 w-5 text-emerald-600" />
                        <h2 className="font-display text-xl font-bold text-foreground">WhatsApp</h2>
                    </div>
                    <Link
                        to="/admin/whatsapp"
                        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary"
                    >
                        Connexion & QR code
                        <ExternalLink className="h-3 w-3" />
                    </Link>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                    En complément de l'email, reçois les notifs sur WhatsApp depuis ton propre numéro.
                    Active ci-dessous et configure le destinataire.
                </p>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <button
                            type="button"
                            onClick={toggleWhatsAppGlobal}
                            className={`shrink-0 inline-flex items-center h-7 w-12 rounded-full transition-colors ${
                                data.whatsapp?.enabled ? "bg-emerald-500" : "bg-muted"
                            }`}
                            aria-pressed={data.whatsapp?.enabled}
                        >
                            <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
                                data.whatsapp?.enabled ? "translate-x-6" : "translate-x-1"
                            }`} />
                        </button>
                        <span className="text-sm font-semibold">
                            {data.whatsapp?.enabled ? "Activé" : "Désactivé"}
                        </span>
                    </label>
                </div>
                <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Numéro destinataire (format international)
                    </label>
                    <input
                        type="tel"
                        value={data.whatsapp?.recipient || ""}
                        onChange={(e) => setWhatsAppRecipient(e.target.value)}
                        placeholder="+33612345678"
                        className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono"
                    />
                    <p className="text-xs text-muted-foreground/80 mt-1.5">
                        C'est sur ce numéro que tu recevras chaque notification (peut être différent du compte connecté).
                    </p>
                </div>
            </section>

            {/* Triggers */}
            <section className="rounded-3xl border border-border bg-card p-6 mt-6">
                <div className="flex items-center gap-2 mb-4">
                    <Bell className="h-5 w-5 text-primary" />
                    <h2 className="font-display text-xl font-bold text-foreground">Événements</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                    Active/désactive chaque type. Si WhatsApp est activé globalement, tu peux aussi désactiver ce canal par type.
                </p>
                <ul className="space-y-3">
                    {data.types.map((t) => {
                        const enabled = data.triggers[t.id]?.enabled ?? true;
                        const waPerTrig = data.triggers[t.id]?.whatsapp ?? true;
                        const waGlobal = data.whatsapp?.enabled;
                        return (
                            <li key={t.id} className="rounded-2xl border border-border bg-background p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                                            enabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                                        }`}>
                                            {enabled ? <BellRing className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-foreground">{t.label}</p>
                                            <p className="text-sm text-muted-foreground mt-0.5">{t.description}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => toggle(t.id)}
                                        className={`shrink-0 inline-flex items-center h-7 w-12 rounded-full transition-colors ${
                                            enabled ? "bg-primary" : "bg-muted"
                                        }`}
                                        aria-pressed={enabled}
                                        aria-label={`${enabled ? "Désactiver" : "Activer"} ${t.label}`}
                                    >
                                        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
                                            enabled ? "translate-x-6" : "translate-x-1"
                                        }`} />
                                    </button>
                                </div>
                                {enabled && waGlobal && (
                                    <div className="mt-3 pl-12 flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={waPerTrig}
                                            onChange={() => toggleWhatsAppPerTrigger(t.id)}
                                            className="accent-emerald-500"
                                            id={`wa-${t.id}`}
                                        />
                                        <label htmlFor={`wa-${t.id}`} className="flex items-center gap-1 cursor-pointer text-muted-foreground">
                                            <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
                                            Envoyer aussi via WhatsApp
                                        </label>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </section>

            {/* Actions */}
            <div className="mt-8 flex items-center justify-between gap-3">
                <button
                    onClick={sendTest}
                    disabled={testing || data.recipients.length === 0}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border-2 border-border bg-card hover:border-primary/40 disabled:opacity-50"
                >
                    {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Envoyer un mail de test
                </button>
                <button
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Enregistrer
                </button>
            </div>
        </div>
    );
}
